/**
 * saveTimeout (ARC-14 follow-up)
 *
 * Firestore write promises resolve only when the SERVER acknowledges the write.
 * Offline, the mutation sits in the SDK's local write queue and the promise stays
 * pending forever — it never rejects. `await updateDoc(...)` therefore hangs, and
 * a save UI that waits on it hangs with it: the member sees an indefinite
 * "Saving…" with no error, which is the failure ARC-14 exists to prevent.
 *
 * The read side hides the same problem differently: `getDoc` falls back to the
 * local cache when offline and RESOLVES, so a pre-write read appears to succeed
 * against data that never came from the server.
 *
 * Nothing here retries or queues. A save either confirms, or it says why not.
 */

/* Long enough that a slow-but-working connection still lands, short enough that
   a member is never left guessing. */
export const SAVE_TIMEOUT_MS = 8000;

/* Refused before anything was attempted, so "nothing was saved" is literally true. */
export const OFFLINE_MESSAGE =
  "You appear to be offline, so nothing was saved. Your entries are still here — reconnect and press Save again.";

/* The write may STILL land later (Firestore keeps it queued), so this says
   "could not confirm" and never "was not saved". */
export const TIMEOUT_MESSAGE =
  "We could not confirm your save. Your entries are still here — check your connection and press Save again.";

/** True only when the browser positively reports being offline. */
export const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Reject with `message` if `promise` has not settled within `ms`.
 *
 * The underlying promise is NOT cancelled — there is no way to cancel a Firestore
 * write — so a timeout means "unconfirmed", not "did not happen". The caller must
 * word its error accordingly.
 */
export function withSaveTimeout(
  promise,
  ms = SAVE_TIMEOUT_MS,
  message = TIMEOUT_MESSAGE
) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}
